import { router } from "@inertiajs/react"
import type { Page } from "@inertiajs/core"
import posthog, { type PostHogConfig } from "posthog-js"
import type { SharedProps } from "@/types"

type SharedPage = Page & {
  props: Page["props"] & SharedProps
}

type BrowserErrorReport = {
  error: unknown
  properties?: Record<string, unknown>
}

let initialized = false
let pageTrackingInitialized = false
let identifiedUserId: string | null = null

function posthogKey() {
  return import.meta.env.VITE_POSTHOG_KEY?.trim()
}

function posthogHost() {
  return import.meta.env.VITE_POSTHOG_HOST?.trim() || "https://us.i.posthog.com"
}

function pageUrl(page: Page) {
  return new URL(page.url, window.location.origin).toString()
}

function identifyFromPage(page: SharedPage) {
  const user = page.props.auth.user

  if (!user) {
    if (identifiedUserId) {
      posthog.reset()
      identifiedUserId = null
    }

    return
  }

  const userId = String(user.id)

  if (identifiedUserId === userId) {
    return
  }

  posthog.identify(userId, {
    email: user.email_address,
  })
  identifiedUserId = userId
}

function capturePageview(page: Page) {
  posthog.capture("$pageview", {
    $current_url: pageUrl(page),
    inertia_component: page.component,
  })
}

function installBrowserErrorReporter() {
  window.metimelyReportError = reportAppError

  const pendingReports: BrowserErrorReport[] = window.metimelyPendingErrorReports || []
  window.metimelyPendingErrorReports = []

  pendingReports.forEach(({ error, properties }) => {
    reportAppError(error, properties)
  })
}

function initializePostHog() {
  if (initialized || typeof window === "undefined") {
    return initialized
  }

  const token = posthogKey()

  if (!token) {
    return false
  }

  const config: Partial<PostHogConfig> = {
    api_host: posthogHost(),
    capture_pageview: false,
    capture_pageleave: true,
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
    },
    person_profiles: "identified_only",
  }

  posthog.init(token, config)
  initialized = true
  installBrowserErrorReporter()

  return true
}

export function setupPostHog(initialPage: Page) {
  if (typeof window === "undefined" || pageTrackingInitialized) {
    return
  }

  if (!initializePostHog()) {
    return
  }

  pageTrackingInitialized = true

  identifyFromPage(initialPage as SharedPage)
  capturePageview(initialPage)

  router.on("navigate", (event) => {
    identifyFromPage(event.detail.page as SharedPage)
    capturePageview(event.detail.page)
  })
}

export function reportAppError(error: unknown, properties: Record<string, unknown> = {}) {
  if (typeof window === "undefined" || !initializePostHog()) {
    return
  }

  posthog.captureException(error, {
    ...properties,
    current_url: window.location.href,
  })
}
